import React, { useState, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import InteractiveLearningForm from "../../components/customCoursework/ActivityForms/InteractiveLearningForm/InteractiveLearningForm";
import ResourceForm from "../../components/customCoursework/ActivityForms/ResourceForm/ResourceForm";
import AssignmentForm from "../../components/customCoursework/ActivityForms/AssignmentForm/AssignmentForm";

import ReviewForm from "../../components/customCoursework/ActivityForms/ReviewForm/ReviewForm";
import AssessmentForm from "../../components/customCoursework/ActivityForms/AssessmentForm/AssessmentForm";
import ChooseActivity from "../../components/customCoursework/ChooseActivity/ChooseActivity";
import { Assessment, useGetAllAssessmentsByCourseQuery } from "@axio/graphql/generated/graphql";
import ProjectForm from "../../components/customCoursework/ActivityForms/ProjectForm/ProjectForm";

const ActivityFormContainer = ({
  visible,
  onClose,
  onSave,
  error,
  isSubmitting,
  existingActivity = null,
  courseId,
  activeModuleData,
}) => {
  // Get activity type from existing activity or default to null
  const getActivityType = (): string | null => {
    if (!existingActivity) return null;

    if (existingActivity.type) {
      return existingActivity.type;
    } else if (existingActivity.type === "AITutor") {
      return "Interactive Learning Experience";
    } else if (existingActivity.type === "AxioResource") {
      return "Resource";
    } else if (existingActivity.type === "Assignment") {
      return "Assignment";
    } else if (existingActivity.type === "Project") {
      return "Project";
    } else if (existingActivity.type === "Review") {
      return "Review";
    } else if (existingActivity.type === "Assessment") {
      return "Assessment";
    }

    console.warn("Could not determine activity type, defaulting to Interactive Learning Experience");
    return "Interactive Learning Experience";
  };

  const [step, setStep] = useState<"choose" | "edit">(existingActivity ? "edit" : "choose");
  const [selectedActivityType, setSelectedActivityType] = useState<string | null>(getActivityType());
  const [formError, setFormError] = useState<string>(error || "");

  const isReviewType = selectedActivityType === "Review" || (existingActivity && getActivityType() === "Review");

  const { data: assessmentsData, loading: assessmentsLoading } = useGetAllAssessmentsByCourseQuery({
    variables: { courseId },
    skip: !courseId || !isReviewType,
    fetchPolicy: "network-only",
  });

  // Important: Reset the state when the visibility changes
  useEffect(() => {
    if (visible) {
      // When modal becomes visible, set the initial state based on existingActivity
      setStep(existingActivity ? "edit" : "choose");
      setSelectedActivityType(getActivityType());
      setFormError("");
    }
  }, [visible, existingActivity]);

  // Also reset when existingActivity changes
  useEffect(() => {
    // Reset to choose step if we have no existing activity (new activity)
    if (!existingActivity) {
      setStep("choose");
      setSelectedActivityType(null);
    } else {
      setStep("edit");
      setSelectedActivityType(getActivityType());
    }
  }, [existingActivity]);

  const handleActivityTypeSelect = (type: string): void => {
    setSelectedActivityType(type);
    setStep("edit");
  };

  const handleFormSubmit = async (activityData): Promise<void> => {
    try {
      setFormError("");
      await onSave({
        ...activityData,
        type: selectedActivityType!,
        id: existingActivity?.id,
      });

      // Reset form state after successful save
      if (!existingActivity) {
        // Only reset if this was a new activity (not editing)
        setStep("choose");
        setSelectedActivityType(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to save activity";
      setFormError(errorMessage);
      throw err;
    }
  };

  const handleClose = (): void => {
    // Reset state before closing
    onClose();
  };

  const renderForm = (): React.ReactElement | null => {
    // Common props for all form components
    const formProps = {
      activityData: existingActivity,
      onSave: handleFormSubmit,
      onCancel: handleClose,
      error: formError,
      isSubmitting: isSubmitting || false,
      isNewActivity: !existingActivity?.id,
    };

    switch (selectedActivityType) {
      case "Interactive Learning Experience":
        return <InteractiveLearningForm {...formProps} />;
      case "Resource":
        return <ResourceForm {...formProps} />;
      case "Assignment":
        return <AssignmentForm {...formProps} />;
      case "Project":
        return <ProjectForm {...formProps} />;
      case "Assessment":
        return <AssessmentForm {...formProps} activeModuleData={activeModuleData} courseId={courseId} />;
      case "Review":
        return (
          <ReviewForm
            {...formProps}
            assessmentsData={(assessmentsData?.getAllAssessmentsByCourse?.data || []) as unknown as Assessment[]}
            assessmentsLoading={assessmentsLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CustomModal style={{ padding: -20 }} testID="activity-form-modal" visible={visible} onRequestClose={handleClose}>
      <View style={styles.container} id="activity-form-container">
        {!existingActivity && step === "choose" ? (
          <ChooseActivity
            onSelect={handleActivityTypeSelect}
            onCancel={handleClose}
            activeModuleData={activeModuleData}
          />
        ) : (
          renderForm()
        )}
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    borderRadius: 20,
    height: "100%",
    overflow: "hidden",
  },
});

export default ActivityFormContainer;
